import { useEffect, useState } from 'react'
import type { AccessEmailDomain, AccessHeldPull, AccessSync, AccessSyncPull, AccessSyncStatus, ApiFailure } from '../api/types'
import type { Api } from '../api'
import { Badge, Button, Card, Loading, Notice } from '../components/ui'
import { FailureState } from '../components/FailureState'
import { Fact, Panel, StateDot, ageWords, day, dayTime, failureOf, messageOf } from './accessWords'

/**
 * ACCESS › MINDZER SYNC, under the new permission model (05 section 7.8).
 *
 * Is MBI's copy of Mindzer up to date? Mindzer is asked every hour and access
 * follows the last good update. An update that would change too much is HELD for an
 * admin to look at; "Accept this update" lets the next hourly run apply that one,
 * once. There is no "Run now": the API cannot run the loader (03 r20). Beside it:
 * the email domains whose people may sign in, which the next update reads.
 *
 * Laid out again 27 Oct 2026 with the other Access pages: the answer and its facts
 * in one card (with the held update's changes and Accept there when held), then the
 * updates and the email domains side by side; each update now also says how many
 * people's access it changed, and why a failed fetch failed (review R-24, R-27).
 */
export function AccessSyncTab({ api }: { api: Api }) {
  const [data, setData] = useState<AccessSync | null>(null)
  const [failure, setFailure] = useState<ApiFailure | null>(null)
  const [reload, setReload] = useState(0)
  const [held, setHeld] = useState<AccessHeldPull | null>(null)
  const [accepting, setAccepting] = useState(false)
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [notice, setNotice] = useState<{ tone: 'good' | 'bad'; text: string } | null>(null)

  useEffect(() => {
    let cancelled = false
    setFailure(null)
    api
      .accessSync()
      .then((d) => !cancelled && setData(d))
      .catch((e: unknown) => {
        if (cancelled) return
        setData(null)
        setFailure(failureOf(e))
      })
    return () => {
      cancelled = true
    }
  }, [api, reload])

  const s = data?.status ?? null

  const seeHeld = (thenAccept = false) => {
    if (!s?.heldPullId) return
    api
      .accessHeldPull(s.heldPullId)
      .then((h) => {
        setHeld(h)
        if (thenAccept && h.pull?.canAccept) setAccepting(true)
      })
      .catch((e: unknown) => setNotice({ tone: 'bad', text: messageOf(e) }))
  }
  // Accept only what is on screen: the held update's changes, as this page read them (7.8).
  const heldShown = !!held?.pull && held.pull.pullId === s?.heldPullId

  const accept = () => {
    if (!s?.heldPullId || !reason.trim() || busy) return
    setBusy(true)
    api
      .accessAcceptPull(s.heldPullId, reason.trim())
      .then((r) => {
        setNotice({ tone: 'good', text: r.outcome === 'ALREADY'
          ? 'This update was already accepted. The next hourly run applies it.'
          : `Accepted. The next hourly run applies the ${dayTime(s.heldUtc)} update, once${r.expiresUtc ? `; the acceptance lapses ${dayTime(r.expiresUtc)}` : ''}.` })
        setAccepting(false)
        setReason('')
        setHeld(null)
        setReload((n) => n + 1)
      })
      .catch((e: unknown) => setNotice({ tone: 'bad', text: messageOf(e) }))
      .finally(() => setBusy(false))
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto">
      {notice && <Notice tone={notice.tone} onDismiss={() => setNotice(null)}>{notice.text}</Notice>}
      {failure ? (
        <Card><FailureState failure={failure} onRetry={() => setReload((n) => n + 1)} /></Card>
      ) : !data || !s ? (
        <Card><Loading label="Reading the Mindzer sync…" /></Card>
      ) : (
        <>
          <Card className="flex shrink-0 flex-col gap-4 px-5 py-4">
            <div className={`flex flex-wrap items-start gap-3 rounded-md border px-4 py-3 ${STATE[s.state].look}`}>
              <StateDot ok={STATE[s.state].ok} />
              <div className="flex min-w-0 flex-1 flex-col gap-1">
                <span className="text-(length:--text-lg) font-semibold text-(--color-ink)">{STATE[s.state].title}</span>
                <span className="max-w-[90ch] text-(length:--text-small) leading-relaxed text-(--color-ink-2)">{stateWords(data)}</span>
                {s.acceptanceId !== null && (
                  <span className="text-(length:--text-small) font-medium text-(--color-ink)">
                    An accepted update waits for the next hourly run{s.acceptanceExpiresUtc ? `; the acceptance lapses ${dayTime(s.acceptanceExpiresUtc)}` : ''}.
                  </span>
                )}
              </div>
              {s.state === 'HELD' && (
                <div className="flex shrink-0 flex-wrap gap-2">
                  <Button variant="default" onClick={() => seeHeld()}>See who gains what</Button>
                  {s.heldCanAccept && !accepting && (
                    <Button variant="primary" onClick={() => (heldShown && held?.pull?.canAccept ? setAccepting(true) : seeHeld(true))}>
                      Accept this update…
                    </Button>
                  )}
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 gap-2 @3xl:grid-cols-2 @6xl:grid-cols-4">
              <Fact label="Last good update"
                value={s.lastGoodUtc ? dayTime(s.lastGoodUtc) : 'None yet'}
                from={s.minutesSinceGood !== null ? `${ageWords(s.minutesSinceGood)} ago · out of date after ${ageWords(s.staleAfterMinutes)}` : undefined} />
              <Fact label="Last attempt"
                value={s.lastAttemptUtc ? dayTime(s.lastAttemptUtc) : 'None yet'}
                from={s.lastAttemptOutcome ? `${outcomeWords(s.lastAttemptOutcome)}${s.lastAttemptCode ? ` (${s.lastAttemptCode})` : ''}` : undefined} />
              <Fact label="Users in Mindzer"
                value={s.activePeople !== null ? `${s.activePeople.toLocaleString('en-GB')} active` : '—'}
                from={s.activePeopleBefore !== null && s.activePeople !== null
                  ? `${s.activePeopleBefore.toLocaleString('en-GB')} the update before${s.people !== null ? ` · ${s.people.toLocaleString('en-GB')} in all` : ''}` : undefined} />
              <Fact label="Weekly figures"
                value={s.weeklyFiguresThrough ? `Through ${dateOnly(s.weeklyFiguresThrough)}` : '—'}
                from={`${s.weeklyFiguresBuiltUtc ? `built ${dayTime(s.weeklyFiguresBuiltUtc)}` : 'not built'} · D365 code list counted ${s.codeListCountedUtc ? dayTime(s.codeListCountedUtc) : 'never'}`} />
            </div>

            <SyncChecks s={s} last={data?.pulls[0] ?? null} />

            {held?.pull && (
              <Panel title={`The ${dayTime(held.pull.startedUtc)} update would change ${held.pull.changes} ${held.pull.changes === 1 ? 'thing' : 'things'}`} bodyClassName="!px-0 !py-0">
                <div className="max-h-80 overflow-auto">
                  <table className="w-full border-collapse text-(length:--text-small)">
                    <thead className="sticky top-0 bg-(--color-surface-2) text-left text-(--color-ink-3)">
                      <tr>
                        <th className="whitespace-nowrap px-4 py-2 font-medium">Change</th>
                        <th className="whitespace-nowrap px-4 py-2 font-medium">Who or what</th>
                        <th className="px-4 py-2 font-medium">Detail</th>
                      </tr>
                    </thead>
                    <tbody>
                      {held.changes.map((c, i) => (
                        <tr key={i} className="border-t border-(--color-line)">
                          <td className="whitespace-nowrap px-4 py-1.5"><Badge tone={CHANGE_TONE[c.changeKind] ?? 'neutral'}>{changeKindWords(c.changeKind)}</Badge></td>
                          <td className="whitespace-nowrap px-4 py-1.5 font-medium text-(--color-ink)">{c.name ?? (c.mindzerId !== null ? `#${c.mindzerId}` : '—')}</td>
                          <td className="px-4 py-1.5 text-(--color-ink-2)">{c.detail ?? ''}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {!held.pull.canAccept && (
                  <p className="border-t border-(--color-line) px-4 py-2 text-(length:--text-small) text-(--color-ink-3)">{held.pull.outcome === 'REFUSED'
                    ? 'A refused update can never be accepted: Mindzer must send a correct one.'
                    : held.pull.overtaken ? 'A later update has been applied since: this one can no longer be accepted.'
                      : !held.pull.deliveryKept ? 'Its delivery is no longer kept: it can no longer be accepted.' : 'It has been accepted already.'}</p>
                )}
              </Panel>
            )}
            {/* After the changes it accepts, so the reason is given with them in view. */}
            {accepting && (
              <div className="flex max-w-2xl flex-col gap-2 rounded-md border border-(--color-navy-300) bg-(--color-navy-50) px-4 py-3">
                <p className="text-(length:--text-small) text-(--color-ink-2)">
                  This applies the {dayTime(s.heldUtc)} update you are looking at, once. Later updates are checked as usual.
                </p>
                <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} placeholder="Reason Of The Change (required): what makes this update right"
                  className="rounded-md border border-(--color-line-strong) bg-(--color-surface) px-2.5 py-2 text-(length:--text-small) text-(--color-ink)" />
                <div className="flex gap-2">
                  <Button variant="primary" onClick={accept} disabled={busy || !reason.trim() || !heldShown || !held?.pull?.canAccept}>Accept this update</Button>
                  <Button variant="ghost" onClick={() => { setAccepting(false); setReason('') }}>Not now</Button>
                </div>
              </div>
            )}
          </Card>

          <div className="grid shrink-0 grid-cols-1 gap-3 @6xl:grid-cols-[1fr_420px]">
            <Card className="flex flex-col">
              <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 border-b border-(--color-line) px-5 py-3">
                <h3 className="eyebrow">Last {data.pulls.length} updates</h3>
                <span className="text-(length:--text-small) text-(--color-ink-3)">Every hour, on their own. Counts and codes only: no name or email.</span>
              </div>
              <div className="max-h-[480px] overflow-auto">
                <table className="w-full border-collapse text-(length:--text-small)">
                  <thead className="sticky top-0 z-[1] bg-(--color-surface-2) text-left text-(--color-ink-3)">
                    <tr>
                      {['Time', 'Result', 'Users', 'Changes', 'Access changed', 'Took', 'Note'].map((h) => (
                        <th key={h} className={`whitespace-nowrap px-4 py-2 font-medium ${['Users', 'Changes', 'Access changed', 'Took'].includes(h) ? 'text-right' : ''}`}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.pulls.map((p) => <PullRow key={p.pullId} p={p} />)}
                  </tbody>
                </table>
              </div>
            </Card>

            <EmailDomains api={api} />
          </div>
        </>
      )}
    </div>
  )
}

const STATE: Record<AccessSyncStatus['state'], { title: string; ok: boolean | null; look: string }> = {
  UP_TO_DATE: { title: 'Up to date', ok: true, look: 'border-(--color-good)/30 bg-(--color-good)/6' },
  HELD: { title: 'An update is held for your review', ok: null, look: 'border-(--color-warn)/35 bg-(--color-warn)/8' },
  STALE: { title: 'Out of date', ok: null, look: 'border-(--color-warn)/35 bg-(--color-warn)/8' },
  FAILING: { title: 'The last update failed', ok: false, look: 'border-(--color-overdue)/30 bg-(--color-overdue)/6' },
  NEVER: { title: 'No update has run yet', ok: false, look: 'border-(--color-overdue)/30 bg-(--color-overdue)/6' },
}

function stateWords(d: AccessSync): string {
  const s = d.status!
  const good = s.lastGoodUtc ? `Last good update ${dayTime(s.lastGoodUtc)}${s.minutesSinceGood !== null ? `, ${ageWords(s.minutesSinceGood)} ago` : ''}.` : 'No good update yet.'
  switch (s.state) {
    case 'UP_TO_DATE':
      return `${good} Mindzer is asked every hour. Access follows the last good update.`
    case 'HELD':
      return `The ${dayTime(s.heldUtc)} update was held: ${(s.heldDetail ?? s.heldCode ?? 'it would change more than the limits allow').replace(/\.$/, '')}. MBI kept the data from the last good update. Users marked as left in it have already lost access. ${good}`
    case 'STALE':
      return `${good} That is older than ${ageWords(s.staleAfterMinutes)}: access still follows it. Look at the last attempts below.`
    case 'FAILING':
      return `The last attempt, ${dayTime(s.lastAttemptUtc)}, failed${s.lastAttemptCode ? ` (${s.lastAttemptCode})` : ''}. ${good} Access still follows it.`
    default:
      return 'Nobody can enter until the first hourly update brings Mindzer’s users in.'
  }
}

/** The loader's outcomes (050), as it writes them. */
const RESULT: Record<string, { words: string; tone: 'good' | 'neutral' | 'warn' | 'bad' }> = {
  APPLIED: { words: '✓ Applied', tone: 'good' },
  NO_CHANGE: { words: '✓ No change', tone: 'neutral' },
  HELD: { words: '● Held', tone: 'warn' },
  REFUSED: { words: '✗ Refused', tone: 'bad' },
  FETCH_FAILED: { words: '✗ No answer', tone: 'bad' },
  ERROR: { words: '✗ Stopped', tone: 'bad' },
  BUSY: { words: '– Busy, tried later', tone: 'neutral' },
  SKIPPED: { words: '– Skipped (one ran recently)', tone: 'neutral' },
}

const CHANGE_TONE: Record<string, 'good' | 'neutral' | 'warn' | 'bad'> = { WIDEN: 'warn', NARROW: 'neutral', LEFT: 'bad', NEW_PERSON: 'good' }

/** A day the database sent as yyyy-MM-dd, shown as that day in every time zone. */
function dateOnly(d: string): string {
  const [y, m, dd] = d.split('-').map(Number)
  return new Date(Date.UTC(y, m - 1, dd)).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' })
}

function changeKindWords(kind: string): string {
  return ({
    MANAGER_LINK: 'Manager link', NEW_PERSON: 'New user', LEFT: 'Left', HOT_TEAM_LINK: 'Business Team code link', HOT_UNIT_LINK: 'Business Unit code link',
    WIDEN: 'Gains', NARROW: 'Loses',
  } as Record<string, string>)[kind] ?? kind.replace(/_/g, ' ').toLowerCase()
}

/** An update's outcome in words, the same in the tile and in the table; one the screen does not know, as words too. */
function outcomeWords(outcome: string): string {
  return (RESULT[outcome]?.words ?? outcome.replace(/_/g, ' ').toLowerCase()).replace(/^[✓✗●–] /, '')
}

function PullRow({ p }: { p: AccessSyncPull }) {
  const tone = RESULT[p.outcome]?.tone ?? 'neutral'
  // Why it failed, in the order the run log knows it: its own note, Mindzer's answer, the fetch's error.
  const note = p.detail ?? (p.fetchStatus && p.fetchStatus !== 200 ? `Mindzer answered ${p.fetchStatus}` : p.fetchError ?? '')
  return (
    <tr className="border-t border-(--color-line) transition-colors hover:bg-(--color-surface-2)">
      <td className="whitespace-nowrap px-4 py-1.5 text-(--color-ink-2)">{dayTime(p.startedUtc)}</td>
      <td className="whitespace-nowrap px-4 py-1.5"><Badge tone={tone}>{outcomeWords(p.outcome)}</Badge></td>
      <td className="whitespace-nowrap px-4 py-1.5 text-right tabular-nums text-(--color-ink-2)">{p.people?.toLocaleString('en-GB') ?? '—'}</td>
      <td className="whitespace-nowrap px-4 py-1.5 text-right tabular-nums text-(--color-ink-2)">{p.rowsChanged?.toLocaleString('en-GB') ?? '—'}</td>
      <td className="whitespace-nowrap px-4 py-1.5 text-right tabular-nums text-(--color-ink-2)">{p.accessChanged?.toLocaleString('en-GB') ?? '—'}</td>
      <td className="whitespace-nowrap px-4 py-1.5 text-right tabular-nums text-(--color-ink-3)">{p.durationMs !== null ? `${Math.round(p.durationMs / 1000)} s` : '—'}</td>
      <td className="px-4 py-1.5 text-(--color-ink-3)">{note}</td>
    </tr>
  )
}

/* =========================================================================
   SIGN-IN EMAIL DOMAINS (D28): whose people may sign in. The next hourly
   update reads the list.
   ========================================================================= */

function EmailDomains({ api }: { api: Api }) {
  const [domains, setDomains] = useState<AccessEmailDomain[] | null>(null)
  const [failure, setFailure] = useState<ApiFailure | null>(null)
  const [reload, setReload] = useState(0)
  const [draft, setDraft] = useState<{ domain: string; remove: boolean } | null>(null)
  const [effect, setEffect] = useState<Record<string, unknown> | null>(null)
  const [newDomain, setNewDomain] = useState('')
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [notice, setNotice] = useState<{ tone: 'good' | 'bad'; text: string } | null>(null)

  useEffect(() => {
    let cancelled = false
    setFailure(null)
    api.accessEmailDomains().then((d) => !cancelled && setDomains(d.domains)).catch((e: unknown) => !cancelled && setFailure(failureOf(e)))
    return () => {
      cancelled = true
    }
  }, [api, reload])

  const start = (domain: string, remove: boolean) => {
    if (!domain.trim() || busy) return
    // A reason belongs to one draft: a new domain or action starts without one.
    if (!draft || draft.domain !== domain.trim() || draft.remove !== remove) setReason('')
    setBusy(true)
    setNotice(null)
    api
      .accessEmailDomainPreview(domain.trim(), remove)
      .then((e) => { setDraft({ domain: domain.trim(), remove }); setEffect(e.summary) })
      .catch((e: unknown) => setNotice({ tone: 'bad', text: messageOf(e) }))
      .finally(() => setBusy(false))
  }

  const save = () => {
    if (!draft || !reason.trim() || busy) return
    setBusy(true)
    api
      .accessEmailDomainSave(draft.domain, draft.remove, reason.trim())
      .then((r) => {
        setNotice({ tone: 'good', text: r.outcome === 'UNCHANGED'
          ? `${draft.domain} was already listed.`
          : `${draft.remove ? 'Removed' : 'Added'} ${draft.domain}. It takes effect at the next hourly update.` })
        setDraft(null)
        setEffect(null)
        setReason('')
        setNewDomain('')
        setReload((n) => n + 1)
      })
      .catch((e: unknown) => setNotice({ tone: 'bad', text: messageOf(e) }))
      .finally(() => setBusy(false))
  }

  const n = (k: string) => (typeof effect?.[k] === 'number' ? (effect[k] as number) : 0)

  return (
    <Card className="flex flex-col">
      <div className="flex flex-col gap-1 border-b border-(--color-line) px-5 py-3">
        <h3 className="eyebrow">Sign-in email domains{domains ? ` · ${domains.length}` : ''}</h3>
        <p className="text-(length:--text-small) text-(--color-ink-3)">Only users whose Mindzer email is at one of these can enter. A change takes effect at the next hourly update.</p>
      </div>
      <div className="flex flex-col gap-3 px-5 py-3">
        {notice && <Notice tone={notice.tone} onDismiss={() => setNotice(null)}>{notice.text}</Notice>}
        {failure ? (
          <FailureState failure={failure} onRetry={() => setReload((k) => k + 1)} />
        ) : domains === null ? (
          <Loading label="Reading the domains…" />
        ) : (
          <ul className="flex max-h-80 flex-col gap-1.5 overflow-y-auto" aria-label="Sign-in email domains">
            {domains.map((d) => (
              <li key={d.emailDomain} className="flex items-start gap-3 rounded-md border border-(--color-line) px-3 py-2 transition-colors hover:border-(--color-navy-300)">
                <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                  <span className="truncate font-medium text-(--color-ink)" title={d.emailDomain}>@{d.emailDomain}</span>
                  <span className="text-(length:--text-small) text-(--color-ink-3) tabular-nums">
                    {d.activePeople.toLocaleString('en-GB')} active · {d.approved.toLocaleString('en-GB')} approved · {d.signIns.toLocaleString('en-GB')} sign-ins
                  </span>
                  {(d.changedByEmail || d.changedUtc) && (
                    <span className="text-(length:--text-micro) text-(--color-ink-4)">added{d.changedByEmail ? ` by ${d.changedByEmail}` : ''}{d.changedUtc ? ` ${day(d.changedUtc)}` : ''}</span>
                  )}
                </span>
                <Button size="sm" variant="ghost" onClick={() => start(d.emailDomain, true)} disabled={busy}>Remove…</Button>
              </li>
            ))}
          </ul>
        )}
        <div className="flex flex-col gap-1">
          <span className="eyebrow">Add a domain</span>
          <div className="flex flex-wrap items-center gap-2">
            <input value={newDomain} onChange={(e) => setNewDomain(e.target.value)} placeholder="e.g. redachem.com" aria-label="Add a domain"
              className="min-w-0 flex-1 rounded-md border border-(--color-line-strong) bg-(--color-surface) px-2.5 py-1.5 text-(length:--text-small) text-(--color-ink)" />
            <Button variant="default" onClick={() => start(newDomain, false)} disabled={busy || !newDomain.trim()}>Review adding it</Button>
          </div>
        </div>
        {draft && (
          <div className={`flex flex-col gap-2 rounded-md border px-3 py-3 text-(length:--text-small) text-(--color-ink-2) ${
            draft.remove ? 'border-(--color-overdue)/30 bg-(--color-overdue)/5' : 'border-(--color-navy-300) bg-(--color-navy-50)'}`}>
            <p className="font-medium text-(--color-ink)">
              {draft.remove
                ? `Removing @${draft.domain}: ${n('Stops_At_Next_Update') || n('Active_People')} active ${(n('Stops_At_Next_Update') || n('Active_People')) === 1 ? 'user stops' : 'users stop'} being able to sign in at the next update (${n('Approved')} of them approved).`
                : `Adding @${draft.domain}: its users are read as company emails from the next update.`}
            </p>
            <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} placeholder="Reason Of The Change (required)"
              className="rounded-md border border-(--color-line-strong) bg-(--color-surface) px-2.5 py-2 text-(--color-ink)" />
            <div className="flex gap-2">
              <Button variant={draft.remove ? 'danger' : 'primary'} onClick={save} disabled={busy || !reason.trim()}>
                {draft.remove ? `Remove @${draft.domain}` : `Add @${draft.domain}`}
              </Button>
              <Button variant="ghost" onClick={() => { setDraft(null); setEffect(null); setReason('') }}>Cancel</Button>
            </div>
          </div>
        )}
      </div>
    </Card>
  )
}

/** sec.Sync_Setting_MBI's limits, as the status read sends them (17i). */
interface SyncLimits {
  Max_Drop_Pct: number; Vanished_Ids_Min: number; Vanished_Ids_Pct: number; Role_Remap_Min: number; Role_Remap_Pct: number
  Effect_Drop_Min: number; Effect_Drop_Pct: number; Effect_Entries_Drop_Pct: number; Left_Surge_Min: number; Left_Surge_Pct: number
  Widen_Person_Staff: number; Widen_Count: number; Link_Hot_Code_Pct: number
}

/** The hold codes a held update's detail names ("WIDEN_COUNT: ...; EFFECT_DROP: ..."): several can trip at once. */
function heldCodes(detail: string | null, first: string | null): string[] {
  const codes = [...(detail ?? '').matchAll(/(?:^|; )([A-Z_]+): /g)].map((m) => m[1])
  return first && !codes.includes(first) ? [first, ...codes] : codes
}

/**
 * THE CHECKS EVERY UPDATE PASSES, EACH WITH ITS LIMIT (05 7.8; review R-24). The loader
 * (050) refuses an update that fails the first five and holds one that trips the last
 * four for an admin's Accept. The marks judge the NEWEST attempt only (review of 17i): a
 * refusal names its code, a hold every check it tripped; an attempt that never reached
 * the checks (Mindzer did not answer, an error), one that changed nothing, the first
 * landing and a delivery an admin accepted say so rather than claiming a pass, because
 * the loader skips some or all checks for them. An older held update still waiting is
 * its own line. Without the limits (an API before 17i) the list is not shown.
 */
function SyncChecks({ s, last }: { s: AccessSyncStatus; last: AccessSyncPull | null }) {
  let l: SyncLimits | null = null
  try {
    l = s.limitsJson ? (JSON.parse(s.limitsJson) as SyncLimits) : null
  } catch {
    l = null
  }
  if (!l) return null
  const n = (x: unknown) => (typeof x === 'number' ? x.toLocaleString('en-GB') : '?')
  const checks: { title: string; limit: string; codes: string[]; held?: boolean }[] = [
    { title: 'Mindzer answered with lists MBI can read', limit: 'refused when the answer is not JSON, a list is empty, or a field is missing',
      codes: ['NOT_JSON', 'EMPTY:', 'FIELD_MISSING:'] },
    { title: 'Everyone and everything once', limit: 'refused when a user, company, Business Unit, Business Team or role has no ID, an ID comes twice, or two active users share an email',
      codes: ['USER_ID_MISSING', 'DUP_USER_ID', 'DUP_REF_ID:', 'DUP_ACTIVE_EMAIL'] },
    { title: 'No sudden drop', limit: `refused when a count falls by more than ${n(l.Max_Drop_Pct)}% against the last good update`, codes: ['DROP:'] },
    { title: 'Nobody vanishes in bulk', limit: `refused when more than ${n(l.Vanished_Ids_Min)} users and ${n(l.Vanished_Ids_Pct)}% are missing`, codes: ['VANISHED_IDS'] },
    { title: 'Roles do not move in bulk', limit: `refused when more than ${n(l.Role_Remap_Min)} users and ${n(l.Role_Remap_Pct)}% change HR Role`, codes: ['ROLE_REMAP'] },
    { title: 'No wave of leavers', limit: `held when more than ${n(l.Left_Surge_Min)} users and ${n(l.Left_Surge_Pct)}% leave or are switched off`,
      codes: ['LEFT_SURGE'], held: true },
    { title: 'Access does not collapse', limit: `held when the users let in fall by more than ${n(l.Effect_Drop_Min)} and ${n(l.Effect_Drop_Pct)}%, or access lines by more than ${n(l.Effect_Entries_Drop_Pct)}%`,
      codes: ['EFFECT_DROP'], held: true },
    { title: 'No broad gain', limit: `held when more than ${n(l.Widen_Count)} users would see more, or would be paused for approval again `
      + `(more than ${n(l.Widen_Person_Staff)} users under them or staff whose access they take on, or any company)`,
      codes: ['WIDEN_COUNT'], held: true },
    { title: 'No busy code moved', limit: `held when a link moves a D365 code holding more than ${n(l.Link_Hot_Code_Pct)}% of one source's rows, if it moves anyone's access`,
      codes: ['LINK_HOT_CODE'], held: true },
  ]
  const o = s.lastAttemptOutcome
  const lastIsHeld = o === 'HELD' && s.heldPullId !== null && s.heldPullId === s.lastAttemptId
  const stopped = o === 'REFUSED' ? (s.lastAttemptCode ? [s.lastAttemptCode] : [])
    : o === 'HELD' ? (lastIsHeld ? heldCodes(s.heldDetail, s.heldCode) : s.lastAttemptCode ? [s.lastAttemptCode] : []) : []
  const stopping = (codes: string[]) => stopped.some((x) => codes.some((c) => (c.endsWith(':') ? x.startsWith(c) : x === c)))
  const accepted = o === 'APPLIED' && !!last && last.pullId === s.lastAttemptId && /accepted \(acceptance \d+\)/.test(last.detail ?? '')
  const firstLanding = o === 'APPLIED' && s.activePeopleBefore === null
  // Which marks are true: all for an ordinary applied update; only what the loader ran for the rest.
  const ran = (i: number) => (o === 'APPLIED' ? (accepted ? !checks[i].held : firstLanding ? i < 2 : true) : false)
  const aside = o === 'REFUSED' ? `The last update was refused (${stopped.join(', ') || 'no code'}).`
    : o === 'HELD' ? `The last update was held (${stopped.join(', ') || 'no code'}).`
      : o === 'FETCH_FAILED' || o === 'ERROR' ? 'The last attempt never reached the checks: it failed before them.'
        : o === 'NO_CHANGE' ? 'The last delivery changed nothing, so there was nothing new to check.'
          : accepted ? 'The last update was one an admin accepted: the hold checks do not run on it again.'
            : firstLanding ? 'The first update: nothing earlier to compare with, so only the first two checks applied.'
              : o === 'APPLIED' ? 'The last update passed them all.'
                : 'Limits set in MBI; an update failing one never reaches access.'
  const olderHeld = s.heldPullId !== null && !lastIsHeld
  return (
    <Panel title="The checks every update passes" aside={<span className="text-(length:--text-small) text-(--color-ink-3)">{aside}</span>}>
      {olderHeld && (
        <p className="mb-3 text-(length:--text-small) text-(--color-ink-2)">
          An earlier update, {s.heldUtc ? dayTime(s.heldUtc) : 'held before'}, is still held ({heldCodes(s.heldDetail, s.heldCode).join(', ') || 'no code'}) and waits for an admin.
        </p>
      )}
      <ul className="grid grid-cols-1 gap-x-6 gap-y-2 @4xl:grid-cols-2" aria-label="The checks every update passes">
        {checks.map((c, i) => {
          const stop = stopping(c.codes)
          return (
            <li key={c.title} className="flex items-start gap-2.5 text-(length:--text-small)">
              <StateDot ok={stop ? false : ran(i) ? true : null} />
              <span className="flex min-w-0 flex-col">
                <span className="font-medium text-(--color-ink)">
                  {c.title}
                  {stop && <span className="ml-2 font-semibold text-(--color-overdue)">{c.held ? 'held this update' : 'refused this update'}</span>}
                </span>
                <span className="text-(--color-ink-3)">{c.limit}</span>
              </span>
            </li>
          )
        })}
      </ul>
    </Panel>
  )
}
